/**
 * Chat State Management
 * Redux slice for managing chat state including messages, chats list, and UI states
 * @module Chat/Reducer
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Message } from '../../../interfaces/components/chatList';
import { ChatItemType, LinksMetaData } from '../../../interfaces/api/qna';
import { ChatState } from '../../../interfaces/reducers/chat';

/**
 * Initial chat state
 * @type {ChatState}
 */
const initialState: ChatState = {
  messages: [],
  chats: [],
  favChats: [],
  selectedChatId: null,
  isLoading: false,
  isLoadingMessage: false,
  isError: false,
  isErrorVoice: false,
  isLoadingVoice: false,
  isLoadingMessages: false,
  references: [],
  metadata: {},
  selectedChatInFavorite: false,
  tab_id: null,
  selectedChatLayoutDirection: 'ltr',
};

/**
 * Chat Redux slice
 * Manages chat messages, history, favorites, and loading states
 */
export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    /**
     * Sets loading state for messages
     * @param {ChatState} state - Current chat state
     */
    setLoadingMessages: (state) => {
      state.isLoading = true;
      state.isLoadingMessage = true;
      state.isError = false;
    },

    /**
     * Updates messages in state
     * @param {ChatState} state - Current chat state
     * @param {PayloadAction<{ messages: Message[]; metadata?: LinksMetaData }>} action - Action with messages and optional metadata
     */
    setMessages: (state, action: PayloadAction<{ messages: Message[]; metadata?: LinksMetaData }>) => {
      const { messages, metadata } = action.payload;
      state.messages = messages;
      if (metadata) {
        state.metadata = metadata;
      }
      state.isLoadingMessage = false;
      state.isError = false;
    },

    /**
     * Ends loading state for messages
     * @param {ChatState} state - Current chat state
     */
    setLoadingMessagesEnd: (state) => {
      state.isLoadingMessage = false;
      state.isLoading = false;
    },

    /**
     * Sets error state for messages
     * @param {ChatState} state - Current chat state
     */
    setErrorMessages: (state) => {
      state.isLoadingMessage = false;
      state.isLoading = false;
      state.isError = true;
    },

    /**
     * Sets loading state for voice processing
     * @param {ChatState} state - Current chat state
     */
    setLoadingVoice: (state) => {
      state.isLoadingVoice = true;
      state.isErrorVoice = false;
    },

    /**
     * Sets error state for voice processing
     * @param {ChatState} state - Current chat state
     */
    setErrorVoice: (state) => {
      state.isLoadingVoice = false;
      state.isErrorVoice = true;
    },

    /**
     * Updates the chats list and automatically filters favorite chats
     * @param {ChatState} state - Current chat state
     * @param {PayloadAction<ChatItemType[]>} action - Action with chat items array
     */
    setChats: (state, action: PayloadAction<ChatItemType[]>) => {
      state.chats = action.payload;
      // Update favChats from the provided chats
      state.favChats = action.payload.filter((chat) => +chat.in_favorite === 1);
    },

    /**
     * Appends new chats to the existing list
     * @param {ChatState} state - Current chat state
     * @param {PayloadAction<ChatItemType[]>} action - Action with chat items to append
     */
    appendChats: (state, action: PayloadAction<ChatItemType[]>) => {
      state.chats = [...state.chats, ...action.payload];
      // Append any favorite chats from the new data
      const newFavs = action.payload.filter((chat) => +chat.in_favorite === 1);
      state.favChats = [...state.favChats, ...newFavs];
    },

    /**
     * Sets the favorite chats list
     * @param {ChatState} state - Current chat state
     * @param {PayloadAction<ChatItemType[]>} action - Action with favorite chat items
     */
    setFavChats: (state, action: PayloadAction<ChatItemType[]>) => {
      state.favChats = action.payload;
    },

    /**
     * Appends new favorite chats to the existing favorites list
     * @param {ChatState} state - Current chat state
     * @param {PayloadAction<ChatItemType[]>} action - Action with favorite chat items to append
     */
    appendFavChats: (state, action: PayloadAction<ChatItemType[]>) => {
      state.favChats = [...state.favChats, ...action.payload];
    },

    /**
     * Toggles a chat's favorite status in both chats and favChats arrays
     * @param {ChatState} state - Current chat state
     * @param {PayloadAction<{ id: string | number; isFavorite: boolean }>} action - Action with chat ID and favorite status
     */
    toggleChatFavoriteAction: (state, action: PayloadAction<{ id: string | number; isFavorite: boolean }>) => {
      const { id, isFavorite } = action.payload;
      // Update favorite flag in the main chats array
      state.chats = state.chats.map((chat) => (chat.id === id ? { ...chat, in_favorite: isFavorite ? 1 : 0 } : chat));
      if (isFavorite) {
        // If toggled on, add to favChats if not already there
        const chat = state.chats.find((chat) => chat.id === id);
        if (chat && !state.favChats.find((c) => c.id === id)) {
          state.favChats.unshift({ ...chat, in_favorite: 1 });
        }
      } else {
        // If toggled off, remove from favChats
        state.favChats = state.favChats.filter((chat) => chat.id !== id);
      }
      // **Update selectedChatInFavorite immediately if the toggled chat is selected**
      if (+state.selectedChatId === +id) {
        state.selectedChatInFavorite = isFavorite;
      }
    },

    /**
     * Selects a chat and updates the selected chat's favorite status
     * @param {ChatState} state - Current chat state
     * @param {PayloadAction<string>} action - Action with chat ID to select
     */
    selectChat: (state, action: PayloadAction<string>) => {
      state.selectedChatId = action.payload;
      const item = state.chats.find((item) => +item.id === +action.payload);
      state.selectedChatInFavorite = item ? +item.in_favorite === 1 : false;
    },

    /**
     * Creates a new chat and selects it
     * @param {ChatState} state - Current chat state
     * @param {PayloadAction<ChatItemType>} action - Action with new chat item
     */
    createNewChat: (state, action: PayloadAction<ChatItemType>) => {
      state.chats.unshift(action.payload);
      // Add to favChats if the new chat is marked as favorite
      if (+action.payload.in_favorite === 1) {
        state.favChats.unshift(action.payload);
      }
      state.selectedChatId = action.payload.id;
      state.selectedChatInFavorite = false;
    },

    /**
     * Sets loading state for chat messages
     * @param {ChatState} state - Current chat state
     * @param {PayloadAction<boolean>} action - Action with loading state
     */
    setLoadingChatMessages: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMessages = action.payload;
    },

    /**
     * Sets references for the current chat
     * @param {ChatState} state - Current chat state
     * @param {PayloadAction<any[]>} action - Action with references array
     */
    setReferences: (state, action: PayloadAction<any[]>) => {
      state.references = action.payload;
    },

    /**
     * Removes a chat from both chats and favChats arrays
     * @param {ChatState} state - Current chat state
     * @param {PayloadAction<number>} action - Action with chat ID to remove
     */
    removeChat: (state, action: PayloadAction<number>) => {
      // Remove from main chats array
      state.chats = state.chats.filter((chat) => chat.id !== action.payload);
      // Also remove from favChats if it exists
      state.favChats = state.favChats.filter((chat) => chat.id !== action.payload);
      if (state.selectedChatId === action.payload) {
        state.selectedChatId = null;
      }
    },

    /**
     * Toggles the selected chat's favorite status
     * @param {ChatState} state - Current chat state
     */
    toggleSelectedChatFavorite: (state) => {
      state.selectedChatInFavorite = !state.selectedChatInFavorite;
    },

    /**
     * Sets the current tab ID
     * @param {ChatState} state - Current chat state
     * @param {PayloadAction<string | null>} action - Action with tab ID
     */
    setTabId: (state, action: PayloadAction<string | null>) => {
      state.tab_id = action.payload;
    },

    /**
     * Sets the layout direction for the selected chat
     * @param {ChatState} state - Current chat state
     * @param {PayloadAction<string>} action - Action with layout direction (ltr or rtl)
     */
    setSelectedChatLayoutDirection: (state, action: PayloadAction<'ltr' | 'rtl'>) => {
      state.selectedChatLayoutDirection = action.payload;
    },

    /**
     * Resets chat state to initial values
     * @returns {ChatState} Initial chat state
     */
    resetChatState: () => initialState,
  },
});

export const {
  setLoadingMessages,
  setLoadingMessagesEnd,
  setMessages,
  setErrorMessages,
  setLoadingVoice,
  setErrorVoice,
  setChats,
  appendChats,
  setFavChats,
  appendFavChats,
  toggleChatFavoriteAction,
  selectChat,
  createNewChat,
  setLoadingChatMessages,
  setReferences,
  removeChat,
  toggleSelectedChatFavorite,
  setTabId,
  resetChatState,
  setSelectedChatLayoutDirection,
} = chatSlice.actions;

export default chatSlice.reducer;
